import {
  Channels,
  CHANNEL_LINEUP_VIEW
  // Channel
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  FALLBACK_CHNL_ICON_URL
} from "./fallbackData";

// Generated by Copilot- Start - 2026-03-13 23:09:54
export function deepClone<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}
// Generated by Copilot- End - 2026-03-13 23:09:54

export function getSortForViewMode(mode) {
  switch (mode) {
    case CHANNEL_LINEUP_VIEW.HD:
      return Channels.sortByHd;
    case CHANNEL_LINEUP_VIEW.CATEGORY:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELS:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELNUMBER:
      return Channels.sortByChannelNumber;
  }
};

export function orderCategories(categories: string[], sortKeyArray: string[]) {
  const sorted = [];
  // eslint-disable-next-line no-plusplus
  for (let srtIx = 0; srtIx < sortKeyArray.length; srtIx++) {
    if (categories.includes(sortKeyArray[srtIx])) { //
      const ixMatch = categories.indexOf(sortKeyArray[srtIx]);
      const key = categories[ixMatch];
      sorted.push(key);
    }
  }
  return sorted.length > 0 ? sorted : categories;
}

export function getBillingCodeLabel(packageId) {
  if (packageId.includes('ATT_TV_')) {
    return packageId.split('ATT_TV_')[1];
  } else if (packageId.includes('DTV_')) {
    return packageId.split('DTV_')[1];
  }
  return 'NULL';
};

/**
 * @description Create a channel object from local/rsn API data. Aligns
 * the naming with the Channel class and National channel parameters.
 *
 * @param chnl channel object to parse, raw from feed minus chnlPackages adjustments for GP
 * @param localrsnUUID unique identifier for local/rsn channel
 * @param packages chnlPackages, if any/modified
 * @returns
 */
export const buildLocalRsnChannel = (
  chnl,
  localrsnUUID: number,
  packages: string[],
  indicators: any[]
) => {
  // Structural markers derived once from the raw API categoryId ("LOCAL"/"RSN",
  // case-insensitive — same normalization the chlupStore reads apply). Set on the
  // built lineup channel so the chlupStore display builders can detect LOCAL/RSN
  // via the flag instead of string-comparing the scalar `category`. The existing
  // `category` field is preserved untouched (Stage 1).
  const normalizedCategory = String(chnl?.categoryId ?? "").toUpperCase();
  const isLocal = normalizedCategory === "LOCAL";
  const isRsn = normalizedCategory === "RSN";
  const c = {
    uuid: `${localrsnUUID}-${chnl?.label}-${chnl?.chnlNum}`,
    type: "Included",
    name: chnl?.label,
    category: chnl?.categoryId,
    isLocal,
    isRsn,
    icon: chnl?.logo
      ? chnl?.logo.includes("directv.com")
        ? chnl?.logo
        : `https://www.directv.com${chnl?.logo?.startsWith("/") ? "" : "/"}${chnl?.logo}`
      : FALLBACK_CHNL_ICON_URL,
    chnlNum: `${chnl?.chnlNum}`,
    isInclAny: true,
    isAddOnPromoAny: false,
    isStream: chnl?.isStream,
    isSatellite: chnl?.isSatellite,
    callSign: chnl?.callSign || chnl?.callLetter,
    displayText: chnl?.callSign, // for MultiChnlSearch
    chnlPackages: packages?.length ? packages : [],
    indicators,
    ...(isRsn && {
      mlb: chnl?.mlb,
      mls: chnl?.mls,
      nba: chnl?.nba,
      nhl: chnl?.nhl,
      wnba: chnl?.wnba,
      rsntier: chnl?.rsntier
    })
  };
  return c;
}

export const isChannelInGenre = (
  channel: any,
  genreLocalChannelMapping: any, genrePackKey: string,
  dma: string, nielsenDma: string
) => {
  if (genrePackKey === "myhometeam") {
    const isMyHomeTeamEligible = channel?.packageNames?.includes?.("MYHOMETEAM"); // Note -> have to update this with the actual key that Q's team will provide
    return isMyHomeTeamEligible;
  }
  // Local channels filter for rest of the genres
  const isLocalChannel = genreLocalChannelMapping?.find?.(mapChnl => mapChnl?.channel === channel?.network);
  const isLocalBelongToGenre = isLocalChannel ? ![undefined, null, ""]?.includes?.(isLocalChannel?.[genrePackKey]) : false;
  const isLocalChannelInDma = isLocalBelongToGenre && (
    isLocalChannel?.[genrePackKey]?.toLowerCase() === "all" ||
      isLocalChannel?.[genrePackKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.())
  );
  return isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma;
};
export const isNationalForGenre = (
  channel: any,
  genrePackKey: string,
  dma: string,
  nielsenDma: string,
  genreNationalMapping: any
): boolean => {
  const nationalMapping = genreNationalMapping?.find?.(
    mapping => mapping?.channel === channel?.network
  );
  if (!nationalMapping) {
    return false;
  }
  const isLocalBelongToGenre = nationalMapping ? ![undefined, null, ""]?.includes?.(nationalMapping?.[genrePackKey]) : false;
  const isNationalDMA = isLocalBelongToGenre && (
    nationalMapping?.[genrePackKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.())
  );
  return isNationalDMA;
};

export function getMaxPackageDim(
  thElem: HTMLElement,
  ctnrSelector: string, nameSelector: string, chnlCountSelector: string
): number {
  return Array.from(thElem?.querySelectorAll(ctnrSelector))
    .reduce((maxDim, container) => {
      const pkgTextDim =
        (container.querySelector(nameSelector) as HTMLElement)
          ?.getBoundingClientRect();
      const pkgChnlCountDim =
        (container.querySelector(chnlCountSelector) as HTMLElement)
          ?.getBoundingClientRect();

      const pkgMaxDim = Math.max(
        pkgTextDim?.width,
        pkgTextDim?.height,
        pkgChnlCountDim?.width,
        pkgChnlCountDim?.height
      );
      return Math.max(maxDim, pkgMaxDim);
    }, -1);
};

export function getLargestMultipleOf(num: number, multiple: number): number {
  if (num <= 0 || multiple <= 0) {
    return -1;
  }
  const remainder = num % multiple;
  return remainder === 0 ? num : num - remainder;
}
